import type { User } from '../types/user';
import { hasRole } from '../utils/permissions';
import {
  canSeeUsersAdmin,
  canSeeRolesAdmin,
  canSeeUserTypesAdmin,
} from '../utils/adminSectionAccess';

export interface AdminNavItem {
  key: string;
  label: string;
  path: string;
  icon: string;
  roleKey?: string;
  adminUsersSection?: boolean;
  adminUserTypesSection?: boolean;
  adminRolesSection?: boolean;
}

export interface AdminNavSection {
  key: string;
  label: string;
  items: AdminNavItem[];
}

/** Canonical Admin nav grouping for full admins. */
export const ADMIN_NAV_SECTIONS: AdminNavSection[] = [
  {
    key: 'user-management',
    label: 'User Management',
    items: [
      { key: 'users', label: 'Users', path: '/admin/users', icon: 'users', adminUsersSection: true },
      { key: 'teams', label: 'Teams', path: '/admin/teams', icon: 'users' },
      { key: 'roles', label: 'Roles', path: '/admin/roles', icon: 'shield-check', adminRolesSection: true },
      { key: 'user-types', label: 'User Types', path: '/admin/user-types', icon: 'users', adminUserTypesSection: true },
      { key: 'sessions', label: 'Sessions', path: '/admin/sessions', icon: 'key' },
    ],
  },
  {
    key: 'master-data',
    label: 'Master Data',
    items: [
      { key: 'ios', label: 'IOs', path: '/admin/ios', icon: 'folder' },
      { key: 'gls', label: 'GLs', path: '/admin/gls', icon: 'dollar-sign' },
      { key: 'planning-cycles', label: 'Planning Cycles', path: '/admin/planning-cycles', icon: 'calendar-range' },
      { key: 'detail-sheets', label: 'Detail Sheets', path: '/admin/detail-sheets', icon: 'folder-open' },
      { key: 'bridges', label: 'Bridges & One Pagers', path: '/admin/bridges', icon: 'git-compare' },
      { key: 'tags', label: 'Tags', path: '/admin/tags', icon: 'tag' },
      { key: 'op-recharges', label: 'OP Recharges', path: '/admin/op-recharges', icon: 'refresh-cw' },
      // DISABLED: OP Slides (see docs/DISABLED_FEATURES.md)
      // { key: 'op-recharges-slides', label: 'OP Slides', path: '/admin/op-recharges-slides', icon: 'presentation' },
    ],
  },
  {
    key: 'import',
    label: 'Import',
    items: [
      { key: 'import', label: 'File & SAP imports', path: '/admin/import', icon: 'upload' },
      { key: 'directory-import', label: 'Directory Import', path: '/admin/directory-import', icon: 'layers' },
      { key: 'email-ingestion', label: 'Email Ingestion', path: '/admin/email-ingestion', icon: 'mail' },
    ],
  },
  {
    key: 'export',
    label: 'Export',
    items: [
      { key: 'export', label: 'Export', path: '/admin/export', icon: 'file-down' },
    ],
  },
  {
    key: 'logs',
    label: 'Logs',
    items: [
      { key: 'activity-log', label: 'Activity Log', path: '/admin/activity-log', icon: 'activity' },
      { key: 'error-logs', label: 'Error Logs', path: '/admin/error-logs', icon: 'bug' },
      { key: 'security-logs', label: 'Security Logs', path: '/admin/security-logs', icon: 'shield', roleKey: 'admin.security_logs.view' },
    ],
  },
];

export function getDelegatedAdminNavItems(user: User | null): AdminNavItem[] {
  if (!user) return [];
  const out: AdminNavItem[] = [];
  if (hasRole(user, 'feature.imports')) {
    out.push({
      key: 'import',
      label: 'File & SAP imports',
      path: '/admin/import',
      icon: 'upload',
    });
  }
  if (hasRole(user, 'admin.security_logs.view')) {
    out.push({
      key: 'security-logs',
      label: 'Security Logs',
      path: '/admin/security-logs',
      icon: 'shield',
      roleKey: 'admin.security_logs.view',
    });
  }
  return out;
}

export function isFullAdmin(user: User | null): boolean {
  return user?.is_admin === true;
}

export function isAdminNavItemVisible(user: User | null, item: AdminNavItem): boolean {
  if (!user) return false;
  if (user.is_admin) {
    if (item.roleKey && !hasRole(user, item.roleKey)) return false;
    if (item.adminUsersSection && !canSeeUsersAdmin(user)) return false;
    if (item.adminUserTypesSection && !canSeeUserTypesAdmin(user)) return false;
    if (item.adminRolesSection && !canSeeRolesAdmin(user)) return false;
    return true;
  }
  if (item.roleKey && !hasRole(user, item.roleKey)) return false;
  return getDelegatedAdminNavItems(user).some((d) => d.path === item.path);
}

export function getVisibleAdminNavSections(user: User | null): AdminNavSection[] {
  if (!user) return [];

  if (!user.is_admin) {
    const delegated = getDelegatedAdminNavItems(user);
    const sections: AdminNavSection[] = [];
    const importItems = delegated.filter((item) => item.key === 'import');
    const logItems = delegated.filter((item) => item.key === 'security-logs');
    if (importItems.length > 0) {
      sections.push({ key: 'import', label: 'Import', items: importItems });
    }
    if (logItems.length > 0) {
      sections.push({ key: 'logs', label: 'Logs', items: logItems });
    }
    return sections;
  }

  return ADMIN_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => isAdminNavItemVisible(user, item)),
  })).filter((section) => section.items.length > 0);
}

/** Flat dropdown entries with section labels for TabNav. */
export type AdminNavDropdownEntry =
  | { type: 'section'; key: string; label: string }
  | { type: 'item'; item: AdminNavItem };

export function getAdminNavDropdownEntries(user: User | null): AdminNavDropdownEntry[] {
  const sections = getVisibleAdminNavSections(user);
  const entries: AdminNavDropdownEntry[] = [];
  for (const section of sections) {
    entries.push({ type: 'section', key: `section-${section.key}`, label: section.label });
    for (const item of section.items) {
      entries.push({ type: 'item', item });
    }
  }
  return entries;
}

/** First accessible admin subsection (used for /admin redirect and Admin tab label click). */
export function getAdminDefaultPath(user: User | null): string {
  if (!user) return '/admin/users';
  const sections = getVisibleAdminNavSections(user);
  return sections[0]?.items[0]?.path ?? '/admin/users';
}

/** Primary entry when clicking Admin in top nav. */
export function getAdminSectionHomePath(user: User | null): string {
  return getAdminDefaultPath(user);
}

export function canAccessAdminNav(user: User | null): boolean {
  if (!user) return false;
  if (user.is_admin) return true;
  return getDelegatedAdminNavItems(user).length > 0;
}
